import { Badge } from '@dreamtree-org/twreact-ui';
import { Keyboard, Monitor, Usb, ScanLine, Plug, Layers } from 'lucide-react';
import { iconFor } from '../lib/typeIcons';

/**
 * An exact-unit photo (`src`, a signed media URL) or, when the seller has not
 * uploaded one, a placeholder composed from theme tokens. `view` picks the
 * placeholder icon and caption; `icon` is the product type's registry icon
 * (a phone shows a phone, B11-P3), a neutral package without one. `pending` adds a small
 * "Photo pending" caption, so a shopper knows real photos are coming (MERCH-57).
 * `illustrative` marks a generated, AI-made or stock image (MERCH-77, D91) with a top-left
 * "Illustrative image — not this unit" badge (a short "Illustrative" on small thumbnails).
 * `notice` / `noticeShort` change those words, e.g. for a product model's catalog image
 * (MERCH-81, D95: "Catalog image — not this unit", short "Catalog"); the server sends the label.
 */
export const ILLUSTRATIVE_LABEL = 'Illustrative image — not this unit';
const VIEWS = [
  [/keyboard/i, Keyboard],
  [/screen|display/i, Monitor],
  [/port/i, Usb],
  [/serial/i, ScanLine],
  [/accessor|charger/i, Plug],
  [/bottom/i, Layers],
];

export default function UnitImage({ view = 'Front view', src, label, size = 'md', className = '', caption = false, pending = false, icon = 'package', illustrative = false, notice = ILLUSTRATIVE_LABEL, noticeShort = 'Illustrative' }) {
  if (src) {
    const alt = illustrative ? `${label || view} (${notice.toLowerCase()})` : label || view;
    const img = <img src={src} alt={alt} loading="lazy" className={`rounded-lg border border-[var(--app-border)] object-cover w-full ${className}`} />;
    if (!illustrative) return img;
    return (
      <span className="relative block w-full" data-illustrative="true">
        {img}
        <span className="absolute left-1.5 top-1.5" aria-hidden="true">
          <Badge size="xs" variant="warning">{size === 'sm' ? noticeShort : notice}</Badge>
        </span>
      </span>
    );
  }
  const Icon = (VIEWS.find(([re]) => re.test(view)) || [null, iconFor(icon)])[1];
  const iconSize = { sm: 'h-7 w-7', md: 'h-12 w-12', lg: 'h-20 w-20 sm:h-28 sm:w-28' }[size] || 'h-12 w-12';
  return (
    <div
      role="img"
      aria-label={label || view}
      className={`relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border border-[var(--app-border)] bg-gradient-to-br from-primary-50 to-primary-100 text-primary-600 ${className}`}
    >
      <Icon className={iconSize} strokeWidth={1.25} aria-hidden="true" />
      {caption ? <span className="px-2 text-center text-xs font-medium text-primary-700">{view}</span> : null}
      {pending && !caption ? <span className="px-2 text-center text-[11px] font-medium leading-tight text-primary-700">Photo pending</span> : null}
    </div>
  );
}
